import './style.scss';
import { DateTime } from 'luxon';
import type { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import type { TransportMode } from '../../../../../models/transport-mode.enum';
import TransportModeIcon from '../../../../Shared/TransportModeIcon';
import { TransportModeHelper } from '../../../../../helpers/transport-mode.helper';
import checkCircleGreen from '../../../../../../assets/icons/check-circle-green-outline.svg';
import infoCircleBlue from '../../../../../../assets/icons/info-circle-blue-outline.svg';
import exclamationTriangleOrange from '../../../../../../assets/icons/exclamation-triangle-orange-outline.svg';
import exclamationHexagonRed from '../../../../../../assets/icons/exclamation-hexagon-red-outline.svg';
import { TranslationService } from '../../../../../services/translation.service';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import { Status } from '../../../../../hooks/fetch.hook';

function TransportModeTrafficInformation({
  transportMode,
  trafficSituations,
  status,
  hideArrowIcon,
}: {
  transportMode: TransportMode;
  trafficSituations: ITrafficSituation[];
  status: Status;
  hideArrowIcon: boolean;
}) {
  // === VARIABLES ===
  const newTrafficSituations = trafficSituations.filter((ts) => ts.startTime > DateTime.now().minus({ hours: 24 }));
  const olderTrafficSituations = trafficSituations.filter((ts) => !(ts.startTime > DateTime.now().minus({ hours: 24 })));

  // === HELPER FUNCTIONS ===

  function calculateCssClass(): string {
    if (status !== Status.success) return '';
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'severe')) return 'transport-mode-traffic-information--severe';
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'normal')) return 'transport-mode-traffic-information--normal';
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'slight')) return 'transport-mode-traffic-information--slight';
    return 'transport-mode-traffic-information--none';
  }

  function calculateIconUrl(): string {
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'severe')) return DataUrlHelper.encode(exclamationHexagonRed);
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'normal')) return DataUrlHelper.encode(exclamationTriangleOrange);
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'slight')) return DataUrlHelper.encode(infoCircleBlue);
    return DataUrlHelper.encode(checkCircleGreen);
  }

  function calculateIconAltText(): string {
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'severe')) return TranslationService.get('trafficinformationoverviewblock.atleastone.severe');
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'normal')) return TranslationService.get('trafficinformationoverviewblock.atleastone.normal');
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'slight')) return TranslationService.get('trafficinformationoverviewblock.atleastone.slight');
    return '';
  }

  function calculateDescription(type: 'short' | 'long'): string {
    if (status === Status.loading) return TranslationService.get('trafficinformationoverviewblock.loadingdata');
    if (status === Status.failed) return TranslationService.get('trafficinformationoverviewblock.errorloadingdata');
    if (status !== Status.success) return '';
    if (newTrafficSituations.length === 0) return TranslationService.get(`trafficinformationoverviewblock.newtrafficsituations.${type}.none`);
    if (newTrafficSituations.length === 1) return TranslationService.get(`trafficinformationoverviewblock.newtrafficsituations.${type}.singular`);
    return TranslationService.get(`trafficinformationoverviewblock.newtrafficsituations.${type}.plural`).replace('{count}', newTrafficSituations.length.toString());
  }

  return (
    <div className={`transport-mode-traffic-information ${calculateCssClass()} d-flex flex-wrap align-items-center p-2 bg-gray rounded ${hideArrowIcon ? 'arrow-icon-md' : 'arrow-icon'}`}>
      <div className="transport-mode-traffic-information__transport-mode d-flex align-items-center pe-2 ">
        <TransportModeIcon transportMode={transportMode} className="me-1" alt="" />
        <span className="fw-bold text-decoration-underline">{TransportModeHelper.translate(transportMode)}</span>
      </div>
      <div className="d-flex flex-grow-1 justify-content-between align-items-center">
        {status === Status.success && newTrafficSituations.length === 0 && olderTrafficSituations.length === 0 ? (
          <div className="d-flex align-items-center me-2">
            <img src={DataUrlHelper.encode(checkCircleGreen)} alt="" className="transport-mode-traffic-information__icon me-1" />
            <div>{TranslationService.get('trafficinformationoverviewblock.trafficsituations.none')}</div>
          </div>
        ) : (
          <div className="d-flex align-items-center me-2">
            {status === Status.success && <img src={calculateIconUrl()} alt={calculateIconAltText()} className="transport-mode-traffic-information__icon me-1" />}
            <span className="visually-hidden">{calculateDescription('long')}</span>
            <span className="d-sm-none" aria-hidden>
              {calculateDescription('short')}
            </span>
            <span className="d-none d-sm-block" aria-hidden>
              {calculateDescription('long')}
            </span>
          </div>
        )}
        {status === Status.success && olderTrafficSituations.length > 0 && (
          <div>
            <span className="visually-hidden">
              {TranslationService.get('trafficinformationoverviewblock.othertrafficsituations.long').replace('{count}', olderTrafficSituations.length.toString())}
            </span>
            <span aria-hidden>{TranslationService.get('trafficinformationoverviewblock.othertrafficsituations.short').replace('{count}', olderTrafficSituations.length.toString())}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransportModeTrafficInformation;
